/* eslint-disable object-curly-newline */
import React, {
  useRef,
  useReducer,
} from 'react';
import { format } from 'date-fns';

import BillingContactInfo from '../../../common/components/BillingContactInfo';
import SubSection from '../../../common/components/SubSection';
import ConfirmationDetails from '../ConfirmationDetails';
import ConfirmationInstructions from '../ConfirmationInstructions';
import ContactInfo from '../../../common/ContactInfo';
import Button from '../../../common/Button';
import PackageSelect from '../components/PackageSelect/PackageSelect';
import ReviewOrder from '../components/ReviewOrder';
import LoadingScreen from '../../../common/LoadingScreen';

import getActivePackage from '../../helpers/getActivePackage';
import sendGTMEvent from '../../helpers/sendGTMEvent';
import { getEventObject, getTotalPurchasePriceInPennies } from '../helpers/gtmEventHelpers';
import getActivePackageObject from '../../helpers/getActivePackageObject';
import { PaymentInfo, PaymentInfoCTA } from '../../../common/components/PaymentInfo';

import BulkPurchaseReducer from '../BulkPurchaseReducer';
import { createPaymentIntent, updatePaymentIntent } from '../services/PaymentIntentService';
import { createBulkPurchaseCode } from '../services/DiveWirePurchaseService';
import useStripeValidations from '../../../common/hooks/useStripeValidations';
import { useFormControls, isRequired, validateEmailFormat, Validator } from '../../../common/hooks/useFormControls';
import IntroSection from '../../common/IntroSection';
import useBulkPurchaseEffects from '../hooks/useBulkPurchaseEffects';
import { scrollToTopOrError } from '../../../common/helpers/scrollTo';
import displayBackendErrors from '../../../common/helpers/displayBackendErrors';

import styles from './BulkPurchaseContainer.module.scss';
import OfferDetails from '../components/OfferDetails';

const BulkPurchaseContainer = () => {
  const {
    pressReleaseListPrice,
    pressReleaseBPPPaymentIntentAPIUrl,
    bulkPurchaseCodeAPIUrl,
    pressReleaseSubmitUrl,
    pressReleaseContactEmail,
    faqUrl,
  } = window.bulkPurchaseCodeCheckoutFlow;

  const {
    cardFields,
    handleCardNumberChange,
    handleCardExpiryChange,
    handleCardCVCChange,
    validatePaymentInfo,
    confirmCardPayment,
  } = useStripeValidations();

  // Reference used to scroll to top of contact info when next button is pressed
  const contactInfoTop = useRef(null);

  // Initial state set during component mount
  const initialState = {
    // Billing Contact Info
    billingContactInfoNeeded: false,
    // Bulk Purchase
    bulkPurchaseCode: {},
    packages: [],
    activePackage: 0,
    activePackageObject: {},
    numPressReleases: 0,
    // Validation/Navigation
    paid: false,
    isSubmitting: false,
    next: false,
    scrollToTopmostError: false,
    // Stripe
    clientSecret: '',
    paymentIntentId: '',
  };
  const [state, dispatch] = useReducer(BulkPurchaseReducer, initialState);

  // FORM VALIDATORS //
  const requiredField = Validator(isRequired, 'THIS FIELD IS REQUIRED.');
  const validEmail = Validator(validateEmailFormat, 'ENTER A VALID EMAIL.');
  const mustAcceptTerms = Validator(isRequired, 'ACCEPTING TERMS OF USE AND PRIVACY POLICY IS REQUIRED.');

  // FORM FIELDS //
  const formControls = [
    { label: 'Full name:', fieldName: 'purchaser_name', value: '', validators: [requiredField] },
    {
      label: 'Email address:',
      description: 'Enter a professional email address associated with your business domain.',
      fieldName: 'purchaser_email',
      value: '',
      validators: [requiredField, validEmail],
    },
    { label: 'Job title:', fieldName: 'purchaser_title', value: '' },
    { label: 'Company name:', fieldName: 'purchaser_company', value: '', validators: [requiredField] },
    { fieldName: 'billingContactName', value: '' },
    { fieldName: 'billingContactEmail', value: '' },
    { fieldName: 'termsAccepted', value: false, validators: [mustAcceptTerms] },
  ];
  const {
    fields,
    setField,
    setValidators,
    validateField,
    validateForm,
    resetField,
    setError,
  } = useFormControls(formControls);

  const purchaseDate = format(new Date(), 'yyyy-MM-dd');
  const amountOwed = getTotalPurchasePriceInPennies(
    state.numPressReleases,
    state.activePackageObject.price_per_press_release ?? pressReleaseListPrice * 100,
  );

  // Callback used for onChange event of Billing Contact Info form
  const setBillingContactInfoNeededOnChange = (event) => {
    // Clear out form errors when user changes option
    const { value } = event.target;
    setValidators('billingContactEmail', [requiredField, validEmail]);
    setValidators('billingContactName', [requiredField]);
    dispatch({ type: 'setBillingContactInfoNeeded', value: value === 'true' });

    // Clear out billing contact fields if different billing contact was not selected
    if (value !== 'true') {
      resetField('billingContactName');
      resetField('billingContactEmail');
    }
  };

  // API call to divesite db that persists the BulkPurchaseCode
  // Payment intent needs to be updated with dynamic value rather than
  // the sample one hardcoded below
  // This will change component to rendering confirmation page
  const handleCheckoutClick = async () => {
    dispatch({ type: 'showSpinner' });

    // Call updatePaymentIntent once before form validation
    try {
      await updatePaymentIntent(
        pressReleaseBPPPaymentIntentAPIUrl,
        { amount: state.numPressReleases, payment_intent_id: state.paymentIntentId },
      );
    } catch (error) {
      dispatch({ type: 'hideSpinner' });
      // eslint-disable-next-line no-console
      console.error(error);
      throw new Error('Updating payment intent failed');
    }
    const validForm = validateForm();
    const paymentInfoIsValid = validatePaymentInfo();
    if (validForm && paymentInfoIsValid) {
      try {
        // Create the BPC and confirm the CardPayment
        // these both need to resolve before we can move on
        const bulkPurchaseCodeResponse = await createBulkPurchaseCode(
          bulkPurchaseCodeAPIUrl,
          {
            bulk_quantity: state.numPressReleases,
            purchase_date: purchaseDate,
            purchase_price: amountOwed,
            purchaser_company: fields.purchaser_company.value,
            purchaser_email: fields.purchaser_email.value,
            purchaser_name: fields.purchaser_name.value,
            purchaser_title: fields.purchaser_title.value,
            billing_contact_name: fields.billingContactName.value,
            billing_contact_email: fields.billingContactEmail.value,
            payment_intent_id: state.paymentIntentId,
          },
        );
        const confirmCardPaymentResponse = await confirmCardPayment(
          state.clientSecret,
          fields.billingContactName.value
            ? fields.billingContactName.value
            : fields.purchaser_name.value,
          fields.billingContactEmail.value
            ? fields.billingContactEmail.value
            : fields.purchaser_email.value,
        );
        dispatch({ type: 'hideSpinner' });
        dispatch({ type: 'setBulkPurchaseCode', value: bulkPurchaseCodeResponse.data });
        if (confirmCardPaymentResponse.error) {
          dispatch({ type: 'setScrollToTopmostError', value: true });
        } else {
          // show the confirmation page
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          dispatch({ type: 'setPaidStatus', value: true });
          sendGTMEvent(getEventObject(state.activePackageObject, state.paymentIntentId, state.numPressReleases), 'purchase');
        }
      } catch (error) {
        dispatch({ type: 'hideSpinner' });
        displayBackendErrors(error, setError, null, formControls);
        scrollToTopOrError(Object.keys(error?.response?.data));
      }
    } else {
      dispatch({ type: 'hideSpinner' });
      dispatch({ type: 'setScrollToTopmostError', value: true });
    }
  };

  // This will handle the animation once the Next button is clicked
  const handleNextClick = () => {
    sendGTMEvent(getEventObject(state.activePackageObject, state.paymentIntentId, state.numPressReleases), 'begin_checkout');
    dispatch({ type: 'showPaymentInformation' });
    if (contactInfoTop.current) {
      window.scrollTo({ top: contactInfoTop.current.offsetTop - 80, left: 0, behavior: 'smooth' });
    }
  };

  // Helper method to display and set active package
  const handlePackageSelect = (pk, num) => {
    if (state.activePackage === pk) {
      // skip if the same package is selected
      return;
    }
    if (state.activePackageObject.pk) {
      // skip if no active package, this must be the first time
      sendGTMEvent(getEventObject(state.activePackageObject, state.paymentIntentId, state.activePackageObject.minimum_quantity), 'remove');
    }
    dispatch({ type: 'setActivePackage', value: pk });
    dispatch({ type: 'setNumPressReleases', value: num });
    const activePkgObject = getActivePackageObject(pk, state.packages);
    dispatch({ type: 'setActivePackageObject', value: activePkgObject });
    sendGTMEvent(getEventObject(activePkgObject, state.paymentIntentId, num), 'add');
    if (!state.paymentIntentId) {
      createPaymentIntent(
        `${pressReleaseBPPPaymentIntentAPIUrl}`,
        { amount: num },
        // eslint-disable-next-line camelcase
        ({ client_secret, id }) => {
          dispatch({ type: 'setPaymentIntentId', id });
          dispatch({ type: 'setClientSecret', client_secret });
        },
      );
    }
  };

  // Increments numPressReleases selected
  const incrementPressReleases = () => {
    dispatch({ type: 'setNumPressReleases', value: state.numPressReleases + 1 });
    const activePkg = getActivePackage(state.numPressReleases + 1, state.packages);
    dispatch({ type: 'setActivePackage', value: activePkg });
    const activePkgObject = getActivePackageObject(activePkg, state.packages);
    dispatch({ type: 'setActivePackageObject', value: activePkgObject });
  };

  // Decrements numPressReleases selected
  const decrementPressReleases = () => {
    dispatch({ type: 'setNumPressReleases', value: state.numPressReleases - 1 });
    const activePkg = getActivePackage(state.numPressReleases - 1, state.packages);
    dispatch({ type: 'setActivePackage', value: activePkg });
    const activePkgObject = getActivePackageObject(activePkg, state.packages);
    dispatch({ type: 'setActivePackageObject', value: activePkgObject });
  };

  useBulkPurchaseEffects({
    state,
    cardFields,
    dispatch,
    handlePackageSelect,
  });

  const selectPackageDescription = 'Press release can be published at any one of our Industry Dive publications. Publish anytime within one year of purchase.';
  const introHeading = state.paid ? 'Press release purchase confirmed' : 'Purchase Multiple Press Releases';
  const receiptRecipient = fields.billingContactEmail.value
    ? fields.billingContactEmail.value
    : fields.purchaser_email.value;
  return (
    <>
      <div className={`${styles.container} row`}>
        {state.isSubmitting && <LoadingScreen isSubmitting={state.isSubmitting} />}
        <div className={`${styles.introContainer}`}>
          <IntroSection sectionClass={styles.intro} title={introHeading}>
            {state.paid
              ? 'Thank you for your order. We have emailed your receipt to:'
              : 'Plan ahead with your team and pre-purchase press releases for any Industry Dive publication. Select a bundle and save.'}
            {state.paid && (<div><strong>{receiptRecipient}</strong></div>)}
          </IntroSection>
        </div>
        {state.paid && (
          <>
            <ConfirmationDetails code={state.bulkPurchaseCode} />
            <ConfirmationInstructions />
          </>
        )}

        {!state.paid && (
          <div className={`${styles.formContainer}`}>
            <div className={styles.packageWrapper}>
              <div className={styles.packageSelect}>
                <SubSection
                  title="Select number of postings"
                  description={selectPackageDescription}
                  extraSectionClass={styles.packageSelect}
                >
                  <PackageSelect
                    packages={state.packages}
                    handlePackageSelect={handlePackageSelect}
                    activePackage={state.activePackage}
                    contactEmail={pressReleaseContactEmail}
                  />
                  {!state.next && (
                    <div className={styles.nextWrapper} ref={contactInfoTop}>
                      {!state.next && (
                        <Button as="button" type="button" extraClassName="next-button" onClick={() => handleNextClick()}>
                          Next
                        </Button>
                      )}
                    </div>
                  )}
                </SubSection>
              </div>
              <div className={`${styles.priceBox}`}>
                <OfferDetails />
              </div>
            </div>
            <div className={`${styles.contactWrapper} ${state.next && !state.paid ? '' : styles.hidden} large-8 medium-7`}>
              <ContactInfo
                title="Contact Information:"
                description="This information is not displayed with the press release and will be kept private."
                name={fields.purchaser_name}
                email={fields.purchaser_email}
                jobTitle={fields.purchaser_title}
                company={fields.purchaser_company}
                onChange={setField}
                onBlur={validateField}
              />
            </div>
            {state.next && !state.paid && (
              <div className={`large-8 medium-7 ${styles.sectionWrapper}`}>
                {/*
                  Since the PaymentInfo component contains Stripe card elements,
                  the Stripe Elements component must be the parent wrapper at the
                  highest level (index.jsx)
                */}
                <SubSection
                  title="Payment information:"
                  description={(
                    <PaymentInfoCTA
                      cta={(
                        <>
                          Request a purchase order
                          {' '}
                          <a href={faqUrl} target="_blank" rel="noreferrer">here</a>.
                        </>
                      )}
                    />
                  )}
                >
                  <PaymentInfo
                    validateCardNumber={handleCardNumberChange}
                    validateCardExpiry={handleCardExpiryChange}
                    validateCardCVC={handleCardCVCChange}
                    cardFields={cardFields}
                    amountOwed={amountOwed}
                  />
                </SubSection>
                <SubSection title="Billing contact information:">
                  <BillingContactInfo
                    billingContactName={fields.billingContactName}
                    billingContactEmail={fields.billingContactEmail}
                    billingContactInfoNeeded={state.billingContactInfoNeeded}
                    setBillingContactInfoNeededOnChange={setBillingContactInfoNeededOnChange}
                    setField={setField}
                    validateField={validateField}
                  />
                </SubSection>
              </div>
            )}
            {state.next && !state.paid && (
              <div className={`large-8 medium-7 ${styles.sectionWrapper}`}>
                <ReviewOrder
                  incrementPressReleases={incrementPressReleases}
                  decrementPressReleases={decrementPressReleases}
                  numPressReleases={state.numPressReleases}
                  activePackageObject={state.activePackageObject}
                  activePackage={state.activePackage}
                  termsAccepted={fields.termsAccepted}
                  handleTermsAccept={setField}
                />
              </div>
            )}
          </div>
        )}
        <div className={styles.sectionWrapper}>
          <div className="row large-8 medium-7 columns">
            {state.next && !state.paid && (
              <Button as="button" extraClassName={styles.checkoutButton} type="submit" onClick={handleCheckoutClick}>
                Complete checkout
              </Button>
            )}
          </div>
          <div className="row large-8 medium-7 columns">
            {state.next && state.paid && (
              <Button as="a" extraClassName="post-pressrelease-button" href={pressReleaseSubmitUrl}>
                Post a press release
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkPurchaseContainer;
